import { Checkbox, Divider, Flex, Typography } from 'antd';
import { useState, type ReactElement } from 'react';
import { useReleaseFlag } from '@/hooks';
import type { GlobalFilter, SelectedNetwork } from '@/types';
import type { BaseShow } from '@/types/api/network';
import { AUDIENCE_PAGE } from '@/utils/constants';
import { mixpanelTrack } from '@/utils/mixpanelUtils/asyncMixpanel';
import { MIXPANEL_EVENT, RevenueDashboardWidget } from '@/utils/mixpanelUtils/events';
import BreakdownField from '../DownloadDrawer/BreakdownField';
import DownloadDrawerShell from '../DownloadDrawer/DownloadDrawerShell';
import useCheckboxCardStyle from '../DownloadDrawer/useCheckboxCardStyle';
import useDownloadExport from '../DownloadDrawer/useDownloadExport';
import {
  BREAKDOWN_BY_PERIOD,
  buildAudienceExportPath,
  getPeriodOptions,
  type AudienceReportKey,
} from '../DownloadDrawer/downloadDrawerUtils';
import useIsDownloadRangeRestricted from '@/hooks/useIsDownloadRangeRestricted';
import {
  AUDIENCE_REPORT_OPTIONS,
  applyReportsChange,
  buildDefaultDownloadSettings,
  getEffectiveSettings,
  type AudienceDownloadSettings,
} from './audienceDownloadModel';

export interface AudienceDownloadDrawerProps {
  isOpen: boolean;
  handleClose: () => void;
  selectedNetwork: SelectedNetwork;
  globalFilters: GlobalFilter;
  shows: BaseShow[];
}

/**
 * Drawer for the audience export. Each checked report is one file: a CSV when only one
 * report is selected, and a ZIP when more than one is.
 */
const AudienceDownloadDrawer = ({
  isOpen,
  handleClose,
  selectedNetwork,
  globalFilters,
  shows,
}: AudienceDownloadDrawerProps): ReactElement | null => {
  const getCheckboxCardStyle = useCheckboxCardStyle();
  const flag = useReleaseFlag('audience_page_backfill');
  const [downloadSettings, setDownloadSettings] = useState<AudienceDownloadSettings>(() =>
    buildDefaultDownloadSettings(globalFilters)
  );

  const isRangeRestricted = useIsDownloadRangeRestricted(downloadSettings.dateRange);
  const effectiveSettings = getEffectiveSettings(downloadSettings, isRangeRestricted);
  const isTotalPeriod = effectiveSettings.period === 'all';

  // Uniques isn't available at the total breakdown, so it is never counted or requested there.
  const effectiveReports = isTotalPeriod
    ? effectiveSettings.reports.filter((report) => report !== 'uniques')
    : effectiveSettings.reports;

  const reportOptions = AUDIENCE_REPORT_OPTIONS.map((option) => {
    const disabled = isTotalPeriod && option.value === 'uniques';
    return { ...option, disabled, style: getCheckboxCardStyle(disabled) };
  });

  const { id: networkId } = selectedNetwork;
  const path =
    networkId && effectiveReports.length > 0
      ? buildAudienceExportPath({
          networkId,
          from: effectiveSettings.dateRange.from,
          to: effectiveSettings.dateRange.to,
          breakdown: BREAKDOWN_BY_PERIOD[effectiveSettings.period],
          reports: effectiveReports,
          showIds: effectiveSettings.shows,
          flag,
        })
      : null;

  const trackingProperties = {
    // Only used once `path` is set, which requires a network id.
    networkId: networkId ?? '',
    networkName: selectedNetwork.name,
    shows: effectiveSettings.shows.length === 0 ? 'all' : effectiveSettings.shows,
    start: effectiveSettings.dateRange.from,
    end: effectiveSettings.dateRange.to,
    reports: effectiveReports,
    widget: RevenueDashboardWidget.AudienceDownloadDrawer,
    page: AUDIENCE_PAGE,
  };

  const { isDownloading, download } = useDownloadExport({
    path,
    onSuccess: () =>
      mixpanelTrack(MIXPANEL_EVENT.audience.downloadDrawer.downloadSuccess, trackingProperties),
    onFailure: (errorCode) =>
      mixpanelTrack(MIXPANEL_EVENT.audience.downloadDrawer.downloadFailed, {
        ...trackingProperties,
        errorCode,
      }),
  });

  return (
    <DownloadDrawerShell
      isOpen={isOpen}
      onClose={handleClose}
      title="Download audience data"
      testId="audience-download-drawer"
      shows={shows}
      downloadSettings={downloadSettings}
      setDownloadSettings={setDownloadSettings}
      selectedFileCount={effectiveReports.length}
      isDownloading={isDownloading}
      onDownload={download}
    >
      <BreakdownField
        label="Breakdown by periods"
        options={getPeriodOptions(isRangeRestricted)}
        value={effectiveSettings.period}
        onChange={(period) => setDownloadSettings((previous) => ({ ...previous, period }))}
        style={{ marginTop: 32 }}
      />
      <Divider />
      <Flex vertical gap={8}>
        <Typography.Title level={5} style={{ margin: 0 }}>
          Reports to download
        </Typography.Title>
        <Typography.Text type="secondary">
          This will be for the shows and period selected above
        </Typography.Text>
      </Flex>
      <Checkbox.Group
        aria-label="Reports to download"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: 8,
          marginTop: 16,
        }}
        options={reportOptions}
        value={effectiveReports}
        onChange={(checkedValues) =>
          setDownloadSettings((previous) =>
            applyReportsChange(previous, checkedValues as AudienceReportKey[], isTotalPeriod)
          )
        }
      />
    </DownloadDrawerShell>
  );
};

export default AudienceDownloadDrawer;
